/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import { ArrowDownTrayIcon, ClipboardDocumentIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

import NewUsersList from './NewUsersList';
import UserActivity from './UserActivity';
import LastUsersAction from './LastUsersAction';
import GraphqlService from '../../service/graphqlService';

const getCognitoUsersGroupsCSV = async () => GraphqlService.getCognitoUsersGroupsCSV();

export default function CognitoUsersList() {
  const [loadUsers, setLoadUsers] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const {
    data: csvData,
    error: errorFetching,
    isLoading,
  } = useQuery(['cognitoUsers'], getCognitoUsersGroupsCSV, {
    cacheTime: 300000,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    enabled: loadUsers,
    select: (res) => {
      const rows = res.split('\n').slice(1).map((row) => {
        if (!row) {
          return null;
        }
        const [user, createdDate, email, groups] = row.split(',');
        if (!user || !email) {
          return null;
        }
        const parsedGroups = groups && groups.split(';').map((group) => {
          const parts = group && group.trim().split(':');
          return `${parts[0]}: ${parts[1]}`;
        }).join(', ');
        return {
          user, createdDate, email, groups: parsedGroups,
        };
      });
      return { rows, file: res };
    },
  });

  const getAllUsers = () => {
    const emails = csvData.rows?.filter((row) => row?.email);
    return emails.map((row) => row.email).join(';');
  };

  const downloadCsv = () => {
    const blob = new Blob([csvData.file], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'CognitoUsersList.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };


  const downloadXls = () => {
    if (!csvData?.rows) {
      return;
    }
    const validData = csvData.rows.filter((row) => row !== null);
    const worksheet = XLSX.utils.json_to_sheet(validData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

    const xlsBuffer = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
    const blob = new Blob([xlsBuffer], { type: 'application/vnd.ms-excel' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'CognitoUsersList.xls');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyUsersInClipboard = () => {
    const users = getAllUsers();
    navigator.clipboard.writeText(users);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2) {
      setLoadUsers(true);
    }
  };

  const tabLabels = ["New Users", "Inactive Users", "All Users"];

  function renderContent() {
    if (activeTab === 0) {
      return <NewUsersList />;
    }

    if (activeTab === 1) {
      return (
        <div className="p-4">
          <LastUsersAction />
        </div>
      );
    }

    if (activeTab === 2) {
      if (errorFetching) {
        return <div className="p-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">Error: {errorFetching.message}</div>;
      }

      if (isLoading) {
        return (
          <div className="p-4 text-center">
            <div className="text-lg font-semibold mb-2">Retrieving cognito users, please wait...</div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div className="bg-blue-600 h-2.5 rounded-full animate-pulse" style={{ width: '100%' }}></div>
            </div>
          </div>
        );
      }

      if (csvData?.rows?.length > 0) {
        return (
          <div className="flex flex-col space-y-4 p-4">
            <div className="flex justify-end items-center space-x-2 flex-wrap">
              <button id="btnCSV" type="button" onClick={downloadCsv} className="flex items-center bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out">
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> CSV
              </button>
              <button id="btnXLS" type="button" onClick={downloadXls} className="flex items-center bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out">
                <DocumentTextIcon className="h-5 w-5 mr-2" /> XLS
              </button>
              <button id="btnSendEmail" type="button" onClick={copyUsersInClipboard} className="flex items-center bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out">
                <ClipboardDocumentIcon className="h-5 w-5 mr-2" /> CLIPBOARD EMAILS
              </button>
            </div>

            <div className="overflow-x-auto shadow-lg rounded-lg scrollbar scrollbar-thumb-gray-500 scrollbar-track-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Creation Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {csvData.rows.map((row) => (row && (
                    <tr key={row.user} className="hover:bg-gray-100">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.user}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.createdDate}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.email}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      return <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">Select a date range to start the search</div>;
    }

    if (activeTab === 3) {
      return (
        <div className="p-4">
          <UserActivity />
        </div>
      );
    }

    return null;
  }

  return (
    <div className="min-h-screen">
      <div className="bg-[#F5936B] py-3 px-4">
        <h1 className="text-xl text-white ">USER REPORTS</h1>
      </div>

      <div className="bg-white p-4 shadow-md">
        <div className="flex justify-center border-b border-gray-200">
          {tabLabels.map((label, index) => (
            <button
              key={label}
              onClick={(e) => handleTabChange(e, index)}
              className={`py-4 px-6 block hover:text-blue-500 focus:outline-none font-medium
                ${activeTab === index ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
